import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Chart as ChartJS,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import html2canvas from "html2canvas";
import { formatNum } from "../DataTable";

ChartJS.register(DoughnutController, ArcElement, Tooltip, Legend);


const GENDER_SLICE_COLORS = {
  female: "#5CC7C1",
  male: "#5C74C7",
  other: "#3CC3DF",
  group: "#F4CF3B",
};

const GROUP_SLICE_COLORS = {
  gold: "#F4CF3B",
  platinium: "#52ABE1",
  platinum: "#52ABE1",
  silver: "#434549",
};

const topCustomerStyles = {
  card: {
    boxSizing: "border-box",
    fontFamily: 'Segoe UI',
    background: "#FFFFFF",
    border: "1px solid #E3E3E3",
    borderRadius: 8,
    padding: "24px 16px",
    height: "100%",
  },
  cardHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    paddingBottom: 24,
    marginBottom: 0,
  },
  dragHandle: {
    cursor: "grab",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: 4,
    lineHeight: 0,
    background: "transparent",
    border: "none",
    padding: 0,
  },
  titleRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 0,
    flexWrap: "wrap",
  },
  headWord: {
    fontFamily: 'Segoe UI',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "24px",
    color: "#606060",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
    marginLeft: "auto",
  },
  tableWrap: {
    width: "100%",
    overflow: "auto",
    maxHeight: 420,
  },
  dataTable: {
    width: "100%",
    maxWidth: '100%',
    tableLayout: "fixed",
    borderCollapse: "collapse",
    fontFamily: 'Segoe UI',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "24px",
  },
  thIndex: {
    boxSizing: "border-box",
    width: 40,
    height: 44,
    padding: 8,
    textAlign: "center",
    verticalAlign: "middle",
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "28px",
    color: "#606060",
    borderTop: "1px solid #E3E3E3",
    borderBottom: "2px solid #E3E3E3",
  },
  thSort: {
    boxSizing: "border-box",
    height: 44,
    padding: 8,
    verticalAlign: "middle",
    borderTop: "1px solid #E3E3E3",
    borderBottom: "2px solid #E3E3E3",
  },
  thSortInner: {
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    maxWidth: "100%",
  },
  thLabel: {
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "28px",
    color: "#606060",
  },
  tdIndex: {
    boxSizing: "border-box",
    padding: 8,
    height: 44,
    textAlign: "center",
    verticalAlign: "middle",
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "24px",
    color: "#606060",
    borderBottom: "1px solid #E3E3E3",
  },
  tdName: {
    boxSizing: "border-box",
    padding: 8,
    height: 44,
    textAlign: "left",
    verticalAlign: "middle",
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "24px",
    color: "#606060",
    borderBottom: "1px solid #E3E3E3",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  tdNumCenter: {
    boxSizing: "border-box",
    padding: 8,
    height: 44,
    textAlign: "center",
    verticalAlign: "middle",
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "24px",
    color: "#606060",
    borderBottom: "1px solid #E3E3E3",
  },
  tdNumRight: {
    boxSizing: "border-box",
    padding: 8,
    height: 44,
    textAlign: "right",
    verticalAlign: "middle",
    fontFamily: 'Segoe UI',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "24px",
    color: "#606060",
    borderBottom: "1px solid #E3E3E3",
  },
  topNBtn: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#FFFFFF",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily: 'Segoe UI',
    lineHeight: "normal",
    outline: "none",
  },
  byTypeBtn: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#FFFFFF",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily: 'Segoe UI',
    lineHeight: "normal",
    outline: "none",
  },
  topNChevron: { fontSize: 14, color: "#606060" },
  byTypeChevron: { fontSize: 14, color: "#606060" },
  dots: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#606060",
    fontSize: 22,
    lineHeight: 1,
    padding: 0,
    userSelect: "none",
    outline: "none",
  },
};

const CHART_COLORS = [
  "#378ADD",
  "#1D9E75",
  "#D85A30",
  "#D4537E",
  "#BA7517",
  "#534AB7",
  "#E8C547",
  "#4fc0cf",
  "#7f96dd",
  "#5fc16f",
  "#888780",
];

function rowMetric(row, byAmount) {
  return byAmount
    ? Number(row.salesAmount) || 0
    : Number(row.qty) || 0;
}

function aggregateByField(rows, fieldKey, byAmount) {
  const map = new Map();
  for (const r of rows) {
    if (!r) continue;
    const raw = r[fieldKey];
    const label =
      raw != null && String(raw).trim() !== ""
        ? String(raw).trim()
        : "Other";
    const v = rowMetric(r, byAmount);
    map.set(label, (map.get(label) || 0) + v);
  }
  return Array.from(map.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
}

function topNWithOther(entries, n) {
  const num = parseInt(n, 10);
  if (isNaN(num) || num <= 0 || entries.length <= num) return entries;
  const top = entries.slice(0, num);
  const otherSum = entries.slice(num).reduce((s, e) => s + e.value, 0);
  if (otherSum > 0) top.push({ label: "Other", value: otherSum });
  return top;
}

function TopNDropdown({ value, onChange, variant }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const choices = [10, 20, 30].map((n) => ({
    key: String(n),
    label: `Top ${n}`,
  }));
  const selected = choices.find((c) => c.key === value) || choices[0];
  const btnStyle =
    variant === "topCustomer" ? topCustomerStyles.topNBtn : styles.topNBtn;
  const chevStyle =
    variant === "topCustomer"
      ? topCustomerStyles.topNChevron
      : styles.topNChevron;
  return (
    <div style={styles.topNWrapper} ref={ref}>
      <button style={btnStyle} onClick={() => setOpen(!open)} type="button">
        {selected.label}
        <span style={chevStyle}>▾</span>
      </button>
      {open && (
        <div style={styles.topNMenu}>
          {choices.map((c) => (
            <button
              key={c.key}
              style={{
                ...styles.topNOption,
                background: value === c.key ? "#eef2f3" : "#fff",
                fontWeight: value === c.key ? 600 : 400,
              }}
              onClick={() => {
                onChange(c.key);
                setOpen(false);
              }}
              type="button"
            >
              {c.label}
              {value === c.key && (
                <span style={{ color: "#5CC7C1", marginLeft: "auto" }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ByAmountsDropdown({ value, onChange, variant }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const items = [
    { key: "Amount", label: "By Amount" },
    { key: "Qty", label: "By Qty" },
  ];
  const selected = items.find((i) => i.key === value) || items[0];
  const btnStyle =
    variant === "topCustomer"
      ? topCustomerStyles.byTypeBtn
      : styles.byTypeBtn;
  const chevStyle =
    variant === "topCustomer"
      ? topCustomerStyles.byTypeChevron
      : styles.byTypeChevron;
  return (
    <div style={styles.byTypeWrapper} ref={ref}>
      <button style={btnStyle} onClick={() => setOpen(!open)} type="button">
        {selected.label} <span style={chevStyle}>▾</span>
      </button>
      {open && (
        <div style={styles.byTypeMenu}>
          {items.map((item) => (
            <button
              key={item.key}
              style={{
                ...styles.byTypeOption,
                background: value === item.key ? "#eef2f3" : "#fff",
                fontWeight: value === item.key ? 600 : 400,
              }}
              onClick={() => {
                onChange(item.key);
                setOpen(false);
              }}
              type="button"
            >
              {item.label}
              {value === item.key && (
                <span style={{ color: "#5CC7C1", marginLeft: "auto" }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ThreeDotsMenu({ chartRef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const handlePrint = () => {
    window.print();
    setOpen(false);
  };
  const handleDownload = () => {
    const chart = chartRef?.current;
    if (!chart) return;
    const url = chart.toBase64Image();
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart.png";
    link.click();
    setOpen(false);
  };
  return (
    <div style={styles.menuWrapper} ref={ref}>
      <button
        style={styles.dots}
        onClick={() => setOpen(!open)}
        type="button"
        title="More options"
      >
        ⋮
      </button>
      {open && (
        <div style={styles.menu}>
          <div style={styles.menuIconRow}>
            <button
              style={styles.menuIconBtn}
              onClick={handlePrint}
              title="Print"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </button>
            <button
              style={styles.menuIconBtn}
              onClick={handleDownload}
              title="Download"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <path d="M19 16 v3 M17.5 19 h3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TableDotsMenu({ variant, downloadRef, fileName = "table.png" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const handleDownloadImage = async () => {
    const target = downloadRef?.current;
    if (!target) {
      setOpen(false);
      return;
    }

    try {
      const canvas = await html2canvas(target, {
        backgroundColor: "#ffffff",
        useCORS: true,
        scale: 2,
      });
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      const link = document.createElement("a");
      link.download = fileName;
      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      } else {
        link.href = canvas.toDataURL("image/png");
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      // Keep menu behavior resilient even if capture fails.
      console.error("Failed to download table image", err);
    } finally {
      setOpen(false);
    }
  };
  const dotsStyle =
    variant === "topCustomer" ? topCustomerStyles.dots : styles.dots;
  return (
    <div style={styles.menuWrapper} ref={ref}>
      <button
        style={dotsStyle}
        onClick={() => setOpen(!open)}
        type="button"
        title="More options"
      >
        ⋮
      </button>
      {open && (
        <div style={styles.menu}>
          <div style={styles.menuIconRow}>
            <button
              style={styles.menuIconBtn}
              onClick={() => {
                window.print();
                setOpen(false);
              }}
              title="Print"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </button>
            <button
              style={styles.menuIconBtn}
              onClick={handleDownloadImage}
              title="Download image"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <path d="M19 16 v3 M17.5 19 h3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function parseReportNumber(val) {
  if (val == null || val === "") return null;
  if (typeof val === "number" && Number.isFinite(val)) return val;
  const s = String(val).replace(/,/g, "").trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function formatTableNumber(val, { decimals = null } = {}) {
  const n = parseReportNumber(val);
  if (n == null) return "—";
  if (decimals != null) {
    return n.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  return n.toLocaleString();
}

/** Side-by-side ↑ ↓ thin strokes (up left, down right) — matches design table headers. */
function SortIconPair() {
  const stroke = "#606060";
  return (
    <svg
      width={16}
      height={14}
      viewBox="0 0 16 14"
      aria-hidden
      style={{ flexShrink: 0, display: "block" }}
    >
      <path
        d="M3 9 L3 4 M1 6 L3 4 L5 6"
        fill="none"
        stroke={stroke}
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11 5 L11 10 M9 8 L11 10 L13 8"
        fill="none"
        stroke={stroke}
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SortThFigma({ label, colKey, onSort, align = "left" }) {
  return (
    <th
      style={{
        ...topCustomerStyles.thSort,
        textAlign: align,
        cursor: "pointer",
        userSelect: "none",
      }}
      onClick={() => onSort(colKey)}
    >
      <div style={topCustomerStyles.thSortInner}>
        <span style={topCustomerStyles.thLabel}>{label}</span>
        <span style={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}>
          <SortIconPair />
        </span>
      </div>
    </th>
  );
}

function TopCustomerCard({ rows, onDragStart, onDragEnd }) {
  const tableRef = useRef(null);
  const [topN, setTopN] = useState("10");
  const [byType, setByType] = useState("Amount");
  const [sortCol, setSortCol] = useState("salesAmount");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    const n = parseInt(topN, 10) || 10;
    const list = [...rows].sort((a, b) => {
      const primary =
        byType === "Amount"
          ? (parseReportNumber(b.salesAmount) ?? 0) -
            (parseReportNumber(a.salesAmount) ?? 0)
          : (parseReportNumber(b.qty) ?? 0) - (parseReportNumber(a.qty) ?? 0);
      return primary;
    });
    return list.slice(0, n);
  }, [rows, topN, byType]);

  const displayRows = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...sorted].sort((a, b) => {
      let va;
      let vb;
      if (sortCol === "customerName") {
        va = String(a.customerName ?? "");
        vb = String(b.customerName ?? "");
        return dir * va.localeCompare(vb);
      }
      va = parseReportNumber(a[sortCol]) ?? 0;
      vb = parseReportNumber(b[sortCol]) ?? 0;
      return dir * (va - vb);
    });
  }, [sorted, sortCol, sortDir]);

  function onSort(key) {
    if (sortCol === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(key);
      setSortDir(key === "customerName" ? "asc" : "desc");
    }
  }

  return (
    <div style={topCustomerStyles.card}>
      <div style={topCustomerStyles.cardHeader}>
        <div
          style={topCustomerStyles.dragHandle}
          draggable
          title="Drag to reorder"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <div style={topCustomerStyles.titleRow}>
          <span style={topCustomerStyles.headWord}>Customer</span>
        </div>
        <div style={topCustomerStyles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} variant="topCustomer" />
          <ByAmountsDropdown value={byType} onChange={setByType} variant="topCustomer" />
          <TableDotsMenu
            variant="topCustomer"
            downloadRef={tableRef}
            fileName="top-customers-table.png"
          />
        </div>
      </div>
      <div style={topCustomerStyles.tableWrap}>
        <table ref={tableRef} style={topCustomerStyles.dataTable}>
          <colgroup>
            <col style={{ width: 40 }} />
            <col style={{ width: 320 }} />
            <col style={{ width: 136 }} />
            <col style={{ width: 136 }} />
            <col style={{ width: 136 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={topCustomerStyles.thIndex}>#</th>
              <SortThFigma
                label="Customer name"
                colKey="customerName"
                onSort={onSort}
                align="left"
              />
              <SortThFigma
                label="Invoice"
                colKey="invoices"
                onSort={onSort}
                align="right"
              />
              <SortThFigma
                label="Qty"
                colKey="qty"
                onSort={onSort}
                align="right"
              />
              <SortThFigma
                label="Amounts"
                colKey="salesAmount"
                onSort={onSort}
                align="right"
              />
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, idx) => (
              <tr key={`${row.customerName}-${idx}`}>
                <td style={topCustomerStyles.tdIndex}>{idx + 1}</td>
                <td style={topCustomerStyles.tdName}>{row.customerName}</td>
                <td style={topCustomerStyles.tdNumRight}>
                  {formatTableNumber(row.invoices)}
                </td>
                <td style={topCustomerStyles.tdNumRight}>
                  {formatTableNumber(row.qty)}
                </td>
                <td style={topCustomerStyles.tdNumRight}>
                  {formatTableNumber(row.salesAmount, { decimals: 0 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function donutOptions(total) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
          padding: 12,
          color: "rgba(0, 0, 0, 0.7)",
          font: {
            family: 'Segoe UI',
            size: 12,
            weight: "400",
            style: "normal",
            lineHeight: "15px",
          },
        },
      },
      tooltip: {
        callbacks: {
          label(ctx) {
            const v = ctx.raw || 0;
            const pct =
              total > 0 ? ((v / total) * 100).toFixed(2) : "0";
            return ` ${ctx.label}: ${formatNum(v)} (${pct}%)`;
          },
        },
      },
    },
  };
}

function GenderDonutCard({ rows, onDragStart, onDragEnd }) {
  const chartRef = useRef();
  const [byType, setByType] = useState("Amount");
  const entries = useMemo(
    () => aggregateByField(rows, "gender", byType === "Amount"),
    [rows, byType],
  );
  const total = entries.reduce((s, e) => s + e.value, 0);
  const data = {
    labels: entries.map((e) => e.label),
    datasets: [
      {
        data: entries.map((e) => e.value),
        backgroundColor: entries.map((e, i) => {
          const key = String(e.label ?? "")
            .trim()
            .toLowerCase();
          return GENDER_SLICE_COLORS[key] ?? CHART_COLORS[i % CHART_COLORS.length];
        }),
        borderColor: "#FFFFFF",
        borderWidth: 1,
        cutout: "68%",
      },
    ],
  };
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          title="Drag to reorder"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>Sales by Gender</p>
        <div style={styles.headerRight}>
          <ByAmountsDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <div style={styles.donutWrap}>
          <Doughnut ref={chartRef} data={data} options={donutOptions(total)} />
        </div>
      </div>
    </div>
  );
}

function GroupDonutCard({ rows, onDragStart, onDragEnd }) {
  const chartRef = useRef();
  const entries = useMemo(
    () => aggregateByField(rows, "group", true),
    [rows],
  );
  const total = entries.reduce((s, e) => s + e.value, 0);
  const data = {
    labels: entries.map((e) => e.label),
    datasets: [
      {
        data: entries.map((e) => e.value),
        backgroundColor: entries.map((e, i) => {
          const key = String(e.label ?? "")
            .trim()
            .toLowerCase();
          return GROUP_SLICE_COLORS[key] ?? CHART_COLORS[i % CHART_COLORS.length];
        }),
        borderColor: "#FFFFFF",
        borderWidth: 1,
        cutout: "68%",
      },
    ],
  };
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          title="Drag to reorder"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>Sales by Group</p>
        <div style={styles.headerRight}>
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <div style={styles.donutWrap}>
          <Doughnut ref={chartRef} data={data} options={donutOptions(total)} />
        </div>
      </div>
    </div>
  );
}

function CountryDonutCard({ rows, onDragStart, onDragEnd }) {
  const chartRef = useRef();
  const [topN, setTopN] = useState("10");
  const [byType, setByType] = useState("Amount");
  const entries = useMemo(() => {
    const agg = aggregateByField(
      rows,
      "country",
      byType === "Amount",
    );
    return topNWithOther(agg, parseInt(topN, 10) || 10);
  }, [rows, topN, byType]);
  const total = entries.reduce((s, e) => s + e.value, 0);
  const data = {
    labels: entries.map((e) => e.label),
    datasets: [
      {
        data: entries.map((e) => e.value),
        backgroundColor: entries.map(
          (_, i) => CHART_COLORS[i % CHART_COLORS.length],
        ),
        borderColor: "#FFFFFF",
        borderWidth: 1,
        cutout: "62%",
      },
    ],
  };
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          title="Drag to reorder"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>Sales by Country</p>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByAmountsDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <div style={styles.donutWrap}>
          <Doughnut ref={chartRef} data={data} options={donutOptions(total)} />
        </div>
      </div>
    </div>
  );
}

export default function CustomerSummaryCharts({ rows = [] }) {
  const safeRows = useMemo(() => rows.filter(Boolean), [rows]);
  const [chartOrder, setChartOrder] = useState([
    "topCustomer",
    "salesByGender",
    "salesByGroup",
    "salesByCountry",
  ]);
  const [draggingChart, setDraggingChart] = useState(null);

  const renderChart = {
    topCustomer: (id) => (
      <TopCustomerCard
        rows={safeRows}
        onDragStart={() => setDraggingChart(id)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
    salesByGender: (id) => (
      <GenderDonutCard
        rows={safeRows}
        onDragStart={() => setDraggingChart(id)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
    salesByGroup: (id) => (
      <GroupDonutCard
        rows={safeRows}
        onDragStart={() => setDraggingChart(id)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
    salesByCountry: (id) => (
      <CountryDonutCard
        rows={safeRows}
        onDragStart={() => setDraggingChart(id)}
        onDragEnd={() => setDraggingChart(null)}
      />
    ),
  };

  function handleDrop(targetId) {
    if (!draggingChart || draggingChart === targetId) return;
    const next = [...chartOrder];
    const fromIdx = next.indexOf(draggingChart);
    const toIdx = next.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setChartOrder(next);
    setDraggingChart(null);
  }

  return (
    <div style={styles.root}>
      <div style={styles.grid}>
        {chartOrder.map((cardId) => (
          <div
            key={cardId}
            style={styles.colItem}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(cardId)}
          >
            {renderChart[cardId](cardId)}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  root: { padding: "0 20px 16px" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },
  colItem: { minWidth: 0, position: "relative" },
  dragHandle: {
    cursor: "grab",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: 4,
    lineHeight: 0,
    background: "transparent",
    border: "none",
    padding: 0,
  },
  card: {
    boxSizing: "border-box",
    fontFamily: 'Segoe UI',
    background: "#FFFFFF",
    border: "1px solid #E3E3E3",
    borderRadius: 8,
    padding: "24px 16px",
    height: "100%",
  },
  cardHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    padding: "0 0 24px",
    marginBottom: 0,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
    marginLeft: "auto",
  },
  sectionTitle: {
    fontFamily: 'Segoe UI',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "24px",
    color: "#606060",
    margin: 0,
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  inlineTitle: {
    fontFamily: 'Segoe UI',
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "24px",
    color: "#606060",
    whiteSpace: "nowrap",
  },
  chartWrap: { height: 420, position: "relative" },
  donutWrap: {
    minHeight: 420,
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 10,
    boxSizing: "border-box",
  },
  tableWrap: {
    maxHeight: 320,
    overflow: "auto",
    borderTop: "1px solid #eee",
  },
  dataTable: {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    fontSize: 11,
  },
  th: {
    textAlign: "left",
    padding: "8px 6px",
    borderBottom: "1px solid #eee",
    color: "#6b7280",
    fontWeight: 600,
  },
  thIndex: {
    width: 36,
    textAlign: "center",
    padding: "8px 4px",
    borderBottom: "1px solid #eee",
    color: "#6b7280",
    fontWeight: 600,
  },
  tdIndex: {
    textAlign: "center",
    padding: "8px 4px",
    borderBottom: "1px solid #f3f4f6",
    color: "#374151",
  },
  tdName: {
    textAlign: "left",
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    color: "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  tdNum: {
    textAlign: "right",
    padding: "8px 6px",
    borderBottom: "1px solid #f3f4f6",
    color: "#374151",
  },
  topNWrapper: { position: "relative" },
  topNBtn: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#FFFFFF",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily: 'Segoe UI',
    lineHeight: "normal",
    outline: "none",
  },
  topNChevron: { fontSize: 14, color: "#606060" },
  topNMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: 130,
    zIndex: 20,
    overflow: "hidden",
  },
  topNOption: {
    display: "flex",
    alignItems: "center",
    padding: "9px 14px",
    fontSize: 14,
    cursor: "pointer",
    color: "#606060",
    gap: 4,
    border: "none",
    width: "100%",
    textAlign: "left",
    background: "transparent",
  },
  byTypeWrapper: { position: "relative" },
  byTypeBtn: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 8px",
    cursor: "pointer",
    background: "#FFFFFF",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily: 'Segoe UI',
    lineHeight: "normal",
    outline: "none",
  },
  byTypeChevron: { fontSize: 14, color: "#606060" },
  byTypeMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: 130,
    zIndex: 20,
    overflow: "hidden",
  },
  byTypeOption: {
    display: "flex",
    alignItems: "center",
    padding: "9px 14px",
    fontSize: 14,
    cursor: "pointer",
    color: "#606060",
    gap: 4,
    border: "none",
    width: "100%",
    textAlign: "left",
    background: "transparent",
  },
  menuWrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dots: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#606060",
    fontSize: 22,
    lineHeight: 1,
    padding: 0,
    userSelect: "none",
    outline: "none",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 12px)",
    background: "#fff",
    border: "1px solid #e9e9e9",
    borderRadius: 8,
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.12)",
    padding: "8px 12px",
    zIndex: 50,
    minWidth: 110,
  },
  menuIconRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  menuIconBtn: {
    width: 34,
    height: 34,
    border: "none",
    background: "transparent",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#666",
    padding: 0,
  },
};
